import { useCurrentProjectId } from '@/hooks/use-current-project';
import { useMe } from '@/hooks/use-me';
import {
  BookOpen,
  Bug,
  ClipboardList,
  Cpu,
  Download,
  Gauge,
  LayoutGrid,
  type LucideIcon,
  Radar,
  Settings,
  Workflow,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export interface RailNavItem {
  /** The row's identity, and the ⌘K target when `to` is absent. */
  key: string;
  icon: LucideIcon;
  label: string;
  /** A fuller feature name, shown where the one-word label needs context. */
  hint?: string;
  /** Where the row leads, when that differs from `key` (a scoped list). */
  to?: string;
  /** Open this destination in a new tab instead of replacing the console. */
  newTab?: boolean;
  /**
   * Whether this row describes the location.
   *
   * A predicate rather than a path prefix because the project-scoped rows are
   * reached through two routes each (`/devices/2606-3` and
   * `/projects/1/devices` are the same idea) and because `/projects/1/dashboard`
   * belongs to *Projects*: the project's own dashboard is a section of it, not a
   * second destination beside the console-wide one (spec §27).
   */
  match: (pathname: string) => boolean;
}

/** `/projects/<id>/<section>` — the same section the rail's row opens. */
function inProject(pathname: string, section: string): boolean {
  return new RegExp(`^/projects/[^/]+/${section}(/|$)`).test(pathname);
}

/**
 * The console's destinations, in one place.
 *
 * The rail draws them and ⌘K lists them, so the two can never disagree about
 * where "Events" goes or which row is current.
 *
 * Two groups, and the split is the scope: `primaryItems` is the console itself
 * (the cross-project dashboard and the project list), `projectItems` is the
 * working set — the list pages, which read across every project you can see and
 * so only *default* to the current one, plus cross-project discovery and the
 * two sections that are that project's own.
 *
 * `footerItems` is the third group, pinned to the bottom of the rail: the two
 * things that are about the platform rather than about the work — how to use it
 * (Docs) and how to get the collectors (Download). They sit apart from the
 * navigation on purpose: neither is a place in the console, and both are read
 * once rather than visited daily.
 *
 * Settings closes that group. It is the reader's own preference rather than a
 * destination too — and it is the *only* thing in the console that is about the
 * console rather than about the crash dumps.
 *
 * Members and settings are deliberately not here: they are actions on a project
 * rather than places inside it, and they live on the projects list's row menu,
 * opening as panels (spec §29).
 */
export function useRailNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const projectId = useCurrentProjectId() ?? null;
  const { data: me } = useMe();

  const primaryItems: RailNavItem[] = useMemo(() => {
    const items: RailNavItem[] = [
      {
        key: '/dashboard',
        icon: Gauge,
        label: t('nav.dashboard'),
        match: (p) => p === '/dashboard',
      },
      {
        key: '/projects',
        icon: LayoutGrid,
        label: t('nav.projects'),
        match: (p) => p === '/projects' || /^\/projects\/[^/]+\/dashboard$/.test(p),
      },
    ];
    return items;
  }, [t]);

  const projectItems: RailNavItem[] = useMemo(() => {
    // The two list pages exist with and without a project: `?project=` narrows
    // them, and without one they are the cross-project lists. The rest are a
    // project's own pages, so with no project in hand there is nothing for them
    // to open and they are not drawn at all.
    const items: RailNavItem[] = [
      {
        key: '/events',
        to: projectId ? `/events?project=${projectId}` : '/events',
        icon: Bug,
        label: t('nav.events'),
        match: (p) => p === '/events' || p.startsWith('/events/'),
      },
      {
        key: '/cases',
        to: projectId ? `/cases?project=${projectId}` : '/cases',
        icon: ClipboardList,
        label: t('nav.cases'),
        match: (p) => p === '/cases' || p.startsWith('/cases/'),
      },
      {
        key: '/common-defects',
        icon: Radar,
        label: t('nav.common_defects', 'Common'),
        hint: t('common_defects.fact_qualifier', 'Common Defect Detection'),
        match: (p) => p === '/common-defects' || p.startsWith('/common-defects/'),
      },
    ];
    if (!projectId) return items;

    items.push(
      {
        key: '/devices',
        to: `/projects/${projectId}/devices`,
        icon: Cpu,
        label: t('nav.devices'),
        match: (p) => p.startsWith('/devices') || inProject(p, 'devices'),
      },
      {
        key: '/catalog',
        to: `/projects/${projectId}/catalog`,
        icon: Workflow,
        label: t('nav.catalog'),
        match: (p) => inProject(p, 'catalog'),
      },
    );
    return items;
  }, [projectId, t]);

  const footerItems: RailNavItem[] = useMemo(
    () => [
      {
        key: '/docs',
        icon: BookOpen,
        label: t('nav.docs', 'Docs'),
        newTab: true,
        match: (p) => p === '/docs' || p.startsWith('/docs/'),
      },
      {
        key: '/download',
        icon: Download,
        label: t('nav.download', 'Download'),
        match: (p) => p === '/download',
      },
      {
        key: '/settings',
        icon: Settings,
        label: t('nav.settings', 'Settings'),
        match: (p) => p === '/settings',
      },
    ],
    [t],
  );

  const items = useMemo(
    () => [...primaryItems, ...projectItems, ...footerItems],
    [primaryItems, projectItems, footerItems],
  );
  const selected = items.find((n) => n.match(location.pathname));

  const project = me?.projects.find((p) => p.project_public_id === projectId);
  const roleLabelMap: Record<string, string> = {
    pa: t('nav.role_pa', 'PA'),
    engineer: t('nav.role_engineer', 'Engineer'),
    lead: t('nav.role_lead', 'SA Leader'),
    admin: t('nav.role_admin', 'Admin'),
    super_admin: t('nav.role_super_admin', 'Super admin'),
  };

  return {
    primaryItems,
    projectItems,
    footerItems,
    /** Every row, in rail order — what ⌘K lists. */
    items,
    selected,
    projectId,
    roleLabel: project?.effective_role ? roleLabelMap[project.effective_role] : undefined,
  };
}
